import React from 'react';
import { ActionIcon, NumberInput, Select } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import CollapsibleListField from '../../../puck/fields/CollapsibleListField';
import { useSelectedItemProps } from '../../../puck/fields/useSelectedItemProps';
import { notifyColumnPreview } from '../../../utils/tableColumnsRegistry';
import { createColumn, distributeMissingWidths, reconcileColumnsChange } from './tableData';

const CELL_TYPE_OPTIONS = [
  { label: 'Texte', value: 'text' },
  { label: 'Image', value: 'image' },
  { label: 'Vidéo', value: 'video' },
  { label: 'Icône', value: 'icon' },
];

/**
 * Type à afficher dans le select. L'ancien `media` n'est plus une option : une
 * cellule qui le porte se montre pour ce qu'elle contient, et le premier
 * changement l'écrit sous sa forme actuelle.
 */
const displayCellType = (cellType, media) => {
  if (cellType !== 'media') return cellType;
  return media && media.kind && media.kind !== 'image' ? 'video' : 'image';
};

const ALIGN_OPTIONS = [
  { label: 'Par défaut', value: '' },
  { label: 'Début', value: 'start' },
  { label: 'Centre', value: 'center' },
  { label: 'Fin', value: 'end' },
];

const VERTICAL_ALIGN_OPTIONS = [
  { label: 'Par défaut', value: '' },
  { label: 'Haut', value: 'start' },
  { label: 'Milieu', value: 'center' },
  { label: 'Bas', value: 'end' },
];

/** Titre replié : le libellé réel de la colonne, débarrassé de son HTML. */
const columnTitle = (column, index) => String(column?.label || '')
  .replace(/<[^>]+>/g, '')
  .trim() || `Colonne ${index + 1}`;

/**
 * Champ « Colonnes ».
 *
 * Le titre de colonne s'écrit **sur le canvas** : ne restent ici que les
 * attributs. Toute modification de la liste passe par `reconcileColumnsChange`,
 * qui réécrit les lignes dans le même mouvement — une colonne ajoutée sans sa
 * cellule dans chaque ligne décalerait tout le tableau.
 *
 * L'œil de l'en-tête amène le canvas sur la colonne : un tableau large défile
 * horizontalement, et régler une colonne qu'on ne voit pas ne mène nulle part
 * (même service que l'œil des slides du Carrousel).
 */
export default function TableColumnsField({ value, onChange }) {
  const { itemProps, updateItemProps } = useSelectedItemProps();
  const blockId = itemProps.id;

  const handleChange = (nextColumns) => {
    const { columns, rows } = reconcileColumnsChange(value, nextColumns, itemProps.rows);
    // Un seul dispatch : colonnes et lignes forment une seule modification.
    updateItemProps({ columns: distributeMissingWidths(columns), rows });
  };

  return (
    <CollapsibleListField
      value={value}
      onChange={handleChange}
      addLabel="Ajouter une colonne"
      createItem={() => createColumn(value || [])}
      itemTitle={columnTitle}
      minItems={1}
      confirmRemove={(column, index) => (
        `Supprimer « ${columnTitle(column, index)} » ? Les valeurs de cette colonne seront perdues dans toutes les lignes.`
      )}
      renderItemActions={(column, index) => (
        <ActionIcon
          variant="subtle"
          color="editor"
          size="sm"
          aria-label={`Voir la colonne ${columnTitle(column, index)} sur la page`}
          title="Voir cette colonne sur la page"
          onClick={(e) => {
            e.stopPropagation();
            if (blockId) notifyColumnPreview(blockId, index);
          }}
        >
          <FontAwesomeIcon icon={icon({ name: 'eye', style: 'light' })} style={{ fontSize: 14 }} />
        </ActionIcon>
      )}
      renderItem={(item, update) => (
        <>
          <NumberInput
            size="xs"
            label="Largeur (%)"
            description="Vide : la colonne se partage l’espace restant."
            min={0}
            max={100}
            value={item.widthPercent ?? ''}
            onChange={(widthPercent) => update({
              widthPercent: widthPercent === '' ? null : Number(widthPercent),
            })}
          />
          <Select
            size="xs"
            label="Contenu des cellules"
            data={CELL_TYPE_OPTIONS}
            value={displayCellType(item.cellType, null) || 'text'}
            onChange={(cellType) => update({ cellType: cellType || 'text' })}
            comboboxProps={{ withinPortal: true }}
          />
          <Select
            size="xs"
            label="Alignement horizontal"
            data={ALIGN_OPTIONS}
            value={item.textAlign || ''}
            onChange={(textAlign) => update({ textAlign: textAlign || '' })}
            comboboxProps={{ withinPortal: true }}
          />
          <Select
            size="xs"
            label="Alignement vertical"
            data={VERTICAL_ALIGN_OPTIONS}
            value={item.verticalAlign || ''}
            onChange={(verticalAlign) => update({ verticalAlign: verticalAlign || '' })}
            comboboxProps={{ withinPortal: true }}
          />
        </>
      )}
    />
  );
}

export { CELL_TYPE_OPTIONS, columnTitle, displayCellType };
